(() => {
  const DURATION = 500;
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches;

  /**
   * Parse values like -28%, +48%, 84%, x2, –30,3 %.
   * @param {string} str
   */
  const parseCountUpValue = (str) => {
    if (!str || typeof str !== 'string') return null;
    const match = str
      .trim()
      .match(/^([+\-–—x×X])?\s*([\d]+(?:[.,]\d+)?)\s*(.*)$/);
    if (!match) return null;

    const rawPrefix = match[1] || '';
    const prefix = rawPrefix === '–' || rawPrefix === '—' ? '-' : rawPrefix;
    const numStr = match[2].replace(',', '.');
    const number = parseFloat(numStr);
    if (!Number.isFinite(number)) return null;

    const decimals = numStr.includes('.') ? (numStr.split('.')[1] || '').length : 0;
    const suffix = (match[3] || '').trim();

    return { prefix, number, suffix, decimals, original: str.trim() };
  };

  /**
   * @param {{ prefix: string, suffix: string, decimals: number }} parsed
   * @param {number} value
   */
  const formatCountUpValue = (parsed, value) => {
    const abs = Math.abs(value);
    const formatted =
      parsed.decimals > 0 ? abs.toFixed(parsed.decimals) : String(Math.round(abs));
    return `${parsed.prefix}${formatted}${parsed.suffix}`;
  };

  /**
   * @param {HTMLElement} element
   * @param {number} duration
   */
  const animateCountUp = (element, duration) => {
    if (element.dataset.auroraCounted === 'true') return;

    const value = element.getAttribute('data-count-up-value') || element.textContent || '';
    const parsed = parseCountUpValue(value);
    if (!parsed) {
      element.textContent = value;
      return;
    }

    element.dataset.auroraCounted = 'true';

    if (prefersReducedMotion()) {
      element.textContent = formatCountUpValue(parsed, parsed.number);
      return;
    }

    const startValue = 0;
    const endValue = parsed.number;
    let startTime = null;
    let frame = 0;

    element.textContent = formatCountUpValue(parsed, startValue);

    const update = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = startValue + (endValue - startValue) * easeOutQuart;
      element.textContent = formatCountUpValue(parsed, current);
      if (progress < 1) {
        frame = requestAnimationFrame(update);
      } else {
        element.textContent = formatCountUpValue(parsed, endValue);
      }
    };

    if (element._auroraCountFrame) cancelAnimationFrame(element._auroraCountFrame);
    frame = requestAnimationFrame(update);
    element._auroraCountFrame = frame;
  };

  /**
   * @param {ParentNode} scope
   */
  const animateStats = (scope) => {
    scope.querySelectorAll('[data-count-up-value]').forEach((stat) => {
      animateCountUp(/** @type {HTMLElement} */ (stat), DURATION);
    });
  };

  /**
   * Stacked result cards: animate when each card enters view.
   * @param {HTMLElement} root
   */
  const bindStack = (root) => {
    if (root._auroraStackBound) return;
    root._auroraStackBound = true;

    const cards = [...root.querySelectorAll('.aurora-visible__result, [data-aurora-result-pane]')];
    if (!cards.length) return;

    if (!('IntersectionObserver' in window)) {
      animateStats(root);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateStats(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.25, rootMargin: '0px 0px -40px 0px' }
    );

    cards.forEach((card) => io.observe(card));
  };

  /**
   * Legacy tab UI (kept for older section markup).
   * @param {HTMLElement} root
   */
  const bindTabs = (root) => {
    if (root._auroraTabsBound) return;

    const tabs = [...root.querySelectorAll('[data-aurora-result-tab]')];
    const panes = [...root.querySelectorAll('[data-aurora-result-pane]')];
    if (!tabs.length || !panes.length) return;

    root._auroraTabsBound = true;

    const activate = (key, { animate = true } = {}) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.auroraResultTab === key;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panes.forEach((pane) => {
        const active = pane.dataset.auroraResultPane === key;
        pane.classList.toggle('is-active', active);
        if (active && animate) animateStats(pane);
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activate(tab.dataset.auroraResultTab, { animate: true }));
    });

    const activePane =
      panes.find((pane) => pane.classList.contains('is-active')) || panes[0];

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const pane = entry.target.querySelector('.aurora-visible__pane.is-active') || activePane;
            if (pane) animateStats(pane);
            io.disconnect();
          });
        },
        { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
      );
      io.observe(root);
    } else if (activePane) {
      animateStats(activePane);
    }
  };

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-aurora-result-stack]').forEach(bindStack);
    scope.querySelectorAll('[data-aurora-result-tabs]').forEach((root) => {
      if (root.querySelector('[data-aurora-result-tab]')) bindTabs(root);
      else if (!root.closest('[data-aurora-result-stack]')) bindStack(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target instanceof HTMLElement ? event.target : document);
  });
})();
